/**
 * Checkout Page - Enhanced with Delivery Mode, Time Windows, and Quote Gating
 * Features:
 * - Delivery Mode: Address OR PUDO selection
 * - Delivery Window: Time slot selection with blackout validation
 * - Calculate Shipping button (quote only when all required fields selected)
 * - Saved Addresses support
 * - Saved Cards support (Stripe)
 * - Feature flags for gradual rollout
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import StripePayment from '../components/StripePayment';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import ScheduledDelivery from '../components/ScheduledDelivery';
import api from '../lib/api';
import { 
  Link2, 
  ArrowLeft,
  MapPin,
  CreditCard,
  CheckCircle,
  Tag,
  Loader2,
  Coins,
  Truck,
  Gift,
  Wallet,
  Banknote,
  Shield,
  Clock,
  Zap,
  Rocket,
  Building2,
  Home,
  Calendar,
  AlertCircle,
  Calculator,
  RefreshCw,
  Star,
  Plus,
  Trash2,
  Scale
} from 'lucide-react';

// Feature Flags
const SAVED_ADDRESSES_ENABLED = true;
const SAVED_CARDS_ENABLED = true;
const DELIVERY_TIME_BLACKOUT_ENABLED = true;
const MARKETPLACE_SHIPPING_OPTIONS_REQUIRED = true;

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [cart, setCart] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [paymentClientSecret, setPaymentClientSecret] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  // ========== DELIVERY MODE ==========
  const [deliveryMode, setDeliveryMode] = useState('ADDRESS'); // ADDRESS | PUDO
  
  // ========== ADDRESS FIELDS ==========
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [parish, setParish] = useState('Kingston');
  const [saveAddress, setSaveAddress] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedSavedAddress, setSelectedSavedAddress] = useState(null);
  
  // ========== PUDO FIELDS ==========
  const [pudoLocations, setPudoLocations] = useState([]);
  const [dropoffPudoId, setDropoffPudoId] = useState('');
  const [selectedPudo, setSelectedPudo] = useState(null);
  const [pudoParish, setPudoParish] = useState('');
  const [pudoSearch, setPudoSearch] = useState('');
  
  // ========== SHIPPING SPEED ==========
  const [shippingSpeed, setShippingSpeed] = useState(''); // Empty until selected
  const [speedSelected, setSpeedSelected] = useState(false);
  
  // ========== PACKAGE WEIGHT ==========
  const [packageWeight, setPackageWeight] = useState(''); // Weight in lbs (optional)
  
  // ========== DELIVERY WINDOW ==========
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryWindowPreset, setDeliveryWindowPreset] = useState('');
  const [deliveryWindowStart, setDeliveryWindowStart] = useState('');
  const [deliveryWindowEnd, setDeliveryWindowEnd] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [blackoutError, setBlackoutError] = useState('');
  
  // ========== PROMO ==========
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardApplied, setGiftCardApplied] = useState(null);
  const [giftCardLoading, setGiftCardLoading] = useState(false);
  
  // ========== PAYMENT ==========
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedCard, setSelectedSavedCard] = useState(null);
  const [saveCard, setSaveCard] = useState(false);
  const [useScheduledDelivery, setUseScheduledDelivery] = useState(false);
  const [scheduledSlot, setScheduledSlot] = useState(null);
  
  // Phase 11: From Cart page state
  const [applyPoints, setApplyPoints] = useState(location.state?.applyPoints || false);
  const [pointsToApply, setPointsToApply] = useState(location.state?.pointsToApply || 0);
  const [useFreeDelivery, setUseFreeDelivery] = useState(location.state?.useFreeDelivery || false);

  // Delivery window presets
  const deliveryWindowPresets = [
    { id: 'morning', label: 'Morning (9AM - 12PM)', start: '09:00', end: '12:00' },
    { id: 'afternoon', label: 'Afternoon (12PM - 4PM)', start: '12:00', end: '16:00' },
    { id: 'evening', label: 'Evening (4PM - 8PM)', start: '16:00', end: '20:00' },
  ];
  
  // Shipping Options
  const shippingOptions = [
    { 
      id: 'STANDARD', 
      name: 'Standard Delivery', 
      icon: Truck, 
      description: '3-5 business days',
      price: 700,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-500'
    },
    { 
      id: 'EXPRESS', 
      name: 'Express Delivery', 
      icon: Zap, 
      description: '1-2 business days',
      price: 1200,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-500'
    },
    { 
      id: 'SAME_DAY', 
      name: 'Same Day Delivery', 
      icon: Rocket, 
      description: 'Delivered today (order before 2PM)',
      price: 2000,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-500'
    }
  ];
  
  // Payment methods
  const paymentMethods = [
    { id: 'stripe', name: 'Card Payment', icon: CreditCard, description: 'Visa, Mastercard' },
    { id: 'fygaro', name: 'Fygaro', icon: Wallet, description: 'Jamaican Fintech' },
    { id: 'cod', name: 'Cash on Delivery', icon: Banknote, description: 'Pay when delivered' }
  ];

  const parishes = [
    'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
    'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
    'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
  ];

  // Filter PUDO locations by selected parish and search term
  const filteredPudoLocations = pudoLocations.filter(pudo => {
    const matchesParish = !pudoParish || pudo.parish === pudoParish;
    const searchLower = pudoSearch.toLowerCase();
    const matchesSearch = !pudoSearch || 
      (pudo.name && pudo.name.toLowerCase().includes(searchLower)) ||
      (pudo.address && pudo.address.toLowerCase().includes(searchLower)) ||
      (pudo.city && pudo.city.toLowerCase().includes(searchLower));
    return matchesParish && matchesSearch;
  });

  // ========== READY TO QUOTE GATE ==========
  const hasAddress = deliveryMode === 'ADDRESS'
    ? !!street && !!city && !!parish
    : !!dropoffPudoId && !!pudoParish;

  const hasSpeed = ['STANDARD', 'EXPRESS', 'SAME_DAY'].includes(shippingSpeed);

  const hasDeliveryWindow = !!deliveryWindowStart && !!deliveryWindowEnd && !!deliveryDate;

  const isReadyToQuote = hasAddress && hasSpeed && (deliveryMode === 'PUDO' || hasDeliveryWindow) && !blackoutError;

  // ========== RESET QUOTE ON FIELD CHANGE ==========
  useEffect(() => {
    if (checkout) {
      setCheckout(null);
    }
  }, [deliveryMode, street, city, parish, dropoffPudoId, shippingSpeed, deliveryWindowStart, deliveryWindowEnd, deliveryDate]);

  // ========== FETCH DATA ON MOUNT ==========
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Fetch cart
      const cartResponse = await api.get('/cart/');
      setCart(cartResponse.data);
      if (!cartResponse.data.items?.length) {
        toast.error('Cart is empty');
        navigate('/cart');
        return;
      }
      
      // Auto-calculate total weight from cart items
      const items = cartResponse.data.items || [];
      const totalWeight = items.reduce((sum, item) => {
        const itemWeight = item.weight_lbs || item.weight || 0;
        const quantity = item.quantity || 1;
        return sum + (itemWeight * quantity);
      }, 0);
      if (totalWeight > 0) {
        setPackageWeight(totalWeight.toFixed(1));
      }

      // Fetch PUDO locations
      try {
        const pudoResponse = await api.get('/pudo/locations');
        setPudoLocations(pudoResponse.data.locations || []);
      } catch (e) {
        console.log('PUDO locations not available');
      }

      // Fetch saved addresses
      if (SAVED_ADDRESSES_ENABLED) {
        try {
          const addressResponse = await api.get('/saved-addresses/');
          setSavedAddresses(addressResponse.data.addresses || []);
        } catch (e) {
          console.log('Saved addresses not available');
        }
      }

      // Fetch saved cards
      if (SAVED_CARDS_ENABLED) {
        try {
          const cardsResponse = await api.get('/billing/payment-methods');
          setSavedCards(cardsResponse.data.payment_methods || []);
        } catch (e) {
          console.log('Saved cards not available');
        }
      }

    } catch (error) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  // Set coupon from cart state
  useEffect(() => {
    if (location.state?.couponCode) {
      setPromoCode(location.state.couponCode);
      setPromoApplied(true);
    }
  }, [location.state]);

  // ========== DELIVERY WINDOW HANDLERS ==========
  const handleWindowPresetChange = (presetId) => {
    setDeliveryWindowPreset(presetId);
    const preset = deliveryWindowPresets.find(p => p.id === presetId);
    if (preset) {
      setDeliveryWindowStart(preset.start);
      setDeliveryWindowEnd(preset.end);
      setBlackoutError('');
    }
  };

  const handleDateChange = (date) => {
    setDeliveryDate(date);
    setBlackoutError('');
    // Validate against blackouts when we have seller data
    if (DELIVERY_TIME_BLACKOUT_ENABLED && deliveryWindowStart && deliveryWindowEnd) {
      validateBlackout(date, deliveryWindowStart, deliveryWindowEnd);
    }
  };

  const validateBlackout = async (date, start, end) => {
    // This will be validated server-side during quote calculation
    // For now, clear any existing error
    setBlackoutError('');
  };

  // ========== SAVED ADDRESS HANDLERS ==========
  const handleSavedAddressSelect = (addressId) => {
    if (addressId === 'new') {
      setSelectedSavedAddress(null);
      setStreet('');
      setCity('');
      setParish('Kingston');
      return;
    }
    
    const address = savedAddresses.find(a => a.id === addressId);
    if (address) {
      setSelectedSavedAddress(address);
      setStreet(address.street);
      setCity(address.city);
      setParish(address.parish || 'Kingston');
    }
  };

  // ========== PUDO HANDLER ==========
  const handlePudoSelect = (pudoId) => {
    setDropoffPudoId(pudoId);
    const pudo = pudoLocations.find(p => p.id === pudoId);
    setSelectedPudo(pudo);
  };

  // ========== SHIPPING SPEED HANDLER ==========
  const handleSpeedSelect = (speedId) => {
    setShippingSpeed(speedId);
    setSpeedSelected(true);
  };

  const getSelectedShippingOption = () => {
    return shippingOptions.find(opt => opt.id === shippingSpeed) || null;
  };

  // ========== CALCULATE SHIPPING (CLICK TRIGGERED) ==========
  const handleCalculateShipping = async () => {
    if (!isReadyToQuote) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCalculating(true);
    setBlackoutError('');

    try {
      const payload = {
        delivery_mode: deliveryMode,
        delivery_address: deliveryMode === 'ADDRESS' 
          ? { street, city, parish }
          : null,
        dropoff_pudo_id: deliveryMode === 'PUDO' ? dropoffPudoId : null,
        shipping_speed: shippingSpeed,
        delivery_window_start: `${deliveryDate}T${deliveryWindowStart}:00`,
        delivery_window_end: `${deliveryDate}T${deliveryWindowEnd}:00`,
        delivery_instructions: deliveryInstructions || null,
        promo_code: promoCode || null,
      };

      const response = await api.post('/orders/checkout/calculate', payload);
      
      // Check for blackout error
      if (response.data.blackout_error) {
        setBlackoutError(response.data.blackout_error);
        toast.error(response.data.blackout_error);
        return;
      }
      
      setCheckout(response.data);
      
      if (response.data.applied_incentive) {
        setPromoApplied(true);
      }
      
      toast.success('Shipping calculated!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to calculate shipping';
      if (msg.includes('blackout') || msg.includes('unavailable')) {
        setBlackoutError(msg);
      }
      toast.error(msg);
    } finally {
      setCalculating(false);
    }
  };

  const applyGiftCard = async () => {
    if (!giftCardCode.trim()) {
      toast.error("Please enter a gift card code");
      return;
    }
    setGiftCardLoading(true);
    try {
      // First validate the card
      const validateRes = await api.post("/gift-cards/validate", null, { params: { code: giftCardCode } });
      if (validateRes.data.valid) {
        // Apply to order
        const orderTotal = checkout?.total || (checkout?.subtotal - (checkout?.discount_amount || 0) + (checkout?.shipping_cost || 0));
        const applyRes = await api.post("/gift-cards/apply", {
          code: giftCardCode,
          order_total: orderTotal
        });
        setGiftCardApplied({
          code: giftCardCode,
          applied_amount: applyRes.data.applied_amount,
          remaining_balance: applyRes.data.gift_card_balance_remaining,
          remaining_to_pay: applyRes.data.remaining_to_pay
        });
        toast.success(applyRes.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid gift card code");
      setGiftCardApplied(null);
    } finally {
      setGiftCardLoading(false);
    }
  };

  const removeGiftCard = () => {
    setGiftCardApplied(null);
    setGiftCardCode("");
  };

  const applyPromo = async () => {
    if (!promoCode) return;
    if (checkout) {
      // Recalculate with promo
      await handleCalculateShipping();
    } else {
      toast.info('Calculate shipping first to apply promo code');
    }
  };

  // ========== PLACE ORDER ==========
  const handleCheckout = async () => {
    if (!checkout) {
      toast.error('Please calculate shipping first');
      return;
    }

    setProcessing(true);
    try {
      const selectedShipping = getSelectedShippingOption();
      
      const orderPayload = {
        delivery_mode: deliveryMode,
        delivery_address: deliveryMode === 'ADDRESS' 
          ? { street, city, parish }
          : null,
        dropoff_pudo_id: deliveryMode === 'PUDO' ? dropoffPudoId : null,
        payment_provider: paymentMethod,
        promo_code: promoCode || null,
        shipping_speed: shippingSpeed,
        shipping_fee: selectedShipping?.price || 0,
        delivery_window_start: `${deliveryDate}T${deliveryWindowStart}:00`,
        delivery_window_end: `${deliveryDate}T${deliveryWindowEnd}:00`,
        delivery_instructions: deliveryInstructions || null,
        apply_points: applyPoints,
        points_to_apply: pointsToApply,
        use_free_delivery_token: useFreeDelivery,
        save_address: saveAddress,
        save_card: saveCard,
        saved_card_id: selectedSavedCard?.id || null
      };

      const response = await api.post('/orders/checkout', orderPayload);

      if (paymentMethod === 'cod') {
        // Cash on Delivery - complete immediately
        setOrderId(response.data.order_id);
        setOrderComplete(true);
        toast.success('Order placed! Pay on delivery.');
      } else if (response.data.payment_client_secret) {
        // Card payment - show Stripe form to collect card details
        setPaymentClientSecret(response.data.payment_client_secret);
        setOrderId(response.data.order_id);
        setShowPaymentForm(true);
        toast.info('Enter your card details to complete payment');
      } else {
        toast.error('Payment initialization failed. Please try again.');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Checkout failed';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  // Calculate total with shipping
  const calculateTotal = () => {
    if (!checkout) return 0;
    // Use the actual delivery_fee from the checkout calculation, not the static shipping option price
    const shippingFee = useFreeDelivery ? 0 : (checkout.delivery_fee || 0);
    const baseTotal = (checkout.subtotal || 0) - (checkout.discount_amount || 0);
    const pointsDiscount = applyPoints ? pointsToApply : 0;
    return Math.max(0, baseTotal + shippingFee - pointsDiscount);
  };

  // Get minimum date (tomorrow for standard, today for same day)
  const getMinDate = () => {
    const today = new Date();
    if (shippingSpeed === 'SAME_DAY') {
      return today.toISOString().split('T')[0];
    }
    today.setDate(today.getDate() + 1);
    return today.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (orderComplete) {
    const selectedShipping = getSelectedShippingOption();
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-emerald-600 text-white px-6 py-4">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Link2 className="h-6 w-6" />
            <span className="font-bold text-lg">Order Confirmed</span>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-12 text-center">
          <div className="h-24 w-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-2">Your order has been placed successfully.</p>
          <p className="text-sm text-gray-500 font-mono mb-4">Order ID: {orderId?.slice(0, 8)}</p>
          
          {selectedShipping && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${selectedShipping.bgColor} mb-4`}>
              <selectedShipping.icon className={`w-5 h-5 ${selectedShipping.color}`} />
              <span className={`font-medium ${selectedShipping.color}`}>{selectedShipping.name}</span>
            </div>
          )}
          
          {deliveryDate && deliveryWindowPreset && (
            <p className="text-gray-600 mb-8">
              <Clock className="w-4 h-4 inline mr-1" />
              Delivery: {new Date(deliveryDate).toLocaleDateString()} - {deliveryWindowPresets.find(p => p.id === deliveryWindowPreset)?.label}
            </p>
          )}
          
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate('/orders')}
              variant="outline"
              className="border-emerald-500 text-emerald-600 rounded-xl"
            >
              View Orders
            </Button>
            <Button
              onClick={() => navigate('/shop')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl"
            >
              Continue Shopping
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-emerald-600 text-white px-6 py-4 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/cart')} className="text-white hover:bg-emerald-700 p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link2 className="h-6 w-6" />
          <span className="font-bold text-lg">Checkout</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-6 space-y-6" data-testid="checkout-main">
        
        {/* ========== STEP 1: DELIVERY MODE ========== */}
        <div className="bg-white rounded-xl border border-emerald-100 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="h-5 w-5 text-emerald-500" />
            <h2 className="font-bold text-gray-800">Delivery Destination</h2>
            <Badge variant="outline" className="ml-auto">Step 1</Badge>
          </div>
          
          {/* Mode Selection */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              data-testid="delivery-address" onClick={() => setDeliveryMode('ADDRESS')}
              className={`p-4 rounded-xl border-2 transition-all ${
                deliveryMode === 'ADDRESS' 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Home className={`w-6 h-6 mx-auto mb-2 ${deliveryMode === 'ADDRESS' ? 'text-emerald-500' : 'text-gray-400'}`} />
              <p className={`font-medium ${deliveryMode === 'ADDRESS' ? 'text-emerald-700' : 'text-gray-600'}`}>
                Home/Office
              </p>
              <p className="text-xs text-gray-500">Deliver to address</p>
            </button>
            
            <button
              data-testid="delivery-pudo" onClick={() => setDeliveryMode('PUDO')}
              className={`p-4 rounded-xl border-2 transition-all ${
                deliveryMode === 'PUDO' 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Building2 className={`w-6 h-6 mx-auto mb-2 ${deliveryMode === 'PUDO' ? 'text-emerald-500' : 'text-gray-400'}`} />
              <p className={`font-medium ${deliveryMode === 'PUDO' ? 'text-emerald-700' : 'text-gray-600'}`}>
                PUDO Point
              </p>
              <p className="text-xs text-gray-500">Pickup location</p>
            </button>
          </div>

          {/* ADDRESS MODE */}
          {deliveryMode === 'ADDRESS' && (
            <div className="space-y-4">
              {/* Saved Addresses */}
              {SAVED_ADDRESSES_ENABLED && savedAddresses.length > 0 && (
                <div>
                  <Label className="text-gray-700">Saved Addresses</Label>
                  <Select value={selectedSavedAddress?.id || 'new'} onValueChange={handleSavedAddressSelect}>
                    <SelectTrigger className="bg-white border-gray-200 h-11 rounded-lg mt-1">
                      <SelectValue placeholder="Select saved address or enter new" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">
                        <span className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Enter new address
                        </span>
                      </SelectItem>
                      {savedAddresses.map(addr => (
                        <SelectItem key={addr.id} value={addr.id}>
                          <span className="flex items-center gap-2">
                            {addr.is_default && <Star className="w-4 h-4 text-yellow-500" />}
                            {addr.label || addr.street}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-gray-700">Street Address</Label>
                <Input
                  data-testid="street-input"
                  placeholder="123 Main Street"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  className="bg-white border-gray-200 h-11 rounded-lg mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-700">City</Label>
                  <Input
                    data-testid="city-input"
                    placeholder="Kingston"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="bg-white border-gray-200 h-11 rounded-lg mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Parish</Label>
                  <Select value={parish} onValueChange={setParish}>
                    <SelectTrigger className="bg-white border-gray-200 h-11 rounded-lg mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {parishes.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Save Address Checkbox */}
              {SAVED_ADDRESSES_ENABLED && !selectedSavedAddress && (
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="save-address" 
                    checked={saveAddress} 
                    onCheckedChange={setSaveAddress}
                  />
                  <Label htmlFor="save-address" className="text-sm text-gray-600 cursor-pointer">
                    Save this address for future orders
                  </Label>
                </div>
              )}
            </div>
          )}

          {/* PUDO MODE - Enhanced with Parish Filter & Search */}
          {deliveryMode === 'PUDO' && (
            <div className="space-y-4">
              {/* Step 1: Select Parish */}
              <div>
                <Label className="text-gray-700 font-medium">Step 1: Select Parish</Label>
                <Select value={pudoParish} onValueChange={(val) => {
                  setPudoParish(val);
                  setDropoffPudoId('');
                  setSelectedPudo(null);
                  setPudoSearch('');
                }}>
                  <SelectTrigger className="bg-white border-gray-200 h-11 rounded-lg mt-1" data-testid="pudo-parish-select">
                    <SelectValue placeholder="Choose a parish" />
                  </SelectTrigger>
                  <SelectContent>
                    {parishes.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Step 2: Select PUDO Centre */}
              {pudoParish && (
                <div>
                  <Label className="text-gray-700 font-medium">Step 2: Select PUDO Centre</Label>
                  
                  {/* Search Input */}
                  <div className="relative mt-1 mb-2">
                    <Input
                      placeholder="Search PUDO centres..."
                      value={pudoSearch}
                      onChange={(e) => setPudoSearch(e.target.value)}
                      className="bg-white border-gray-200 h-10 rounded-lg pl-9"
                      data-testid="pudo-search-input"
                    />
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  
                  {/* PUDO Dropdown */}
                  <Select value={dropoffPudoId} onValueChange={handlePudoSelect}>
                    <SelectTrigger className="bg-white border-gray-200 h-11 rounded-lg" data-testid="pudo-location-select">
                      <SelectValue placeholder="Select a PUDO location" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {filteredPudoLocations.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          No PUDO locations found in {pudoParish}
                        </div>
                      ) : (
                        filteredPudoLocations.map(pudo => (
                          <SelectItem key={pudo.id} value={pudo.id}>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              <div>
                                <p className="font-medium">{pudo.name}</p>
                                <p className="text-xs text-gray-500">{pudo.address || pudo.city}</p>
                              </div>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Selected PUDO Details */}
              {selectedPudo && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-emerald-800">{selectedPudo.name}</p>
                      <p className="text-sm text-emerald-700">{selectedPudo.address}</p>
                      {selectedPudo.city && <p className="text-sm text-emerald-600">{selectedPudo.city}, {selectedPudo.parish}</p>}
                      {selectedPudo.hours && (
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {selectedPudo.hours}
                        </p>
                      )}
                    </div>
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Validation indicator */}
          <div className={`mt-4 p-2 rounded-lg text-sm flex items-center gap-2 ${hasAddress ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'}`}>
            {hasAddress ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Destination selected
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4" />
                {deliveryMode === 'ADDRESS' ? 'Enter street, city, and parish' : 'Select a PUDO location'}
              </>
            )}
          </div>
        </div>

        {/* ========== STEP 2: SHIPPING SPEED ========== */}
        <div className="bg-white rounded-xl border border-emerald-100 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Truck className="h-5 w-5 text-emerald-500" />
            <h2 className="font-bold text-gray-800">Shipping Speed</h2>
            <Badge variant="outline" className="ml-auto">Step 2</Badge>
          </div>
          <div className="space-y-3">
            {shippingOptions.map(option => {
              const Icon = option.icon;
              const isSelected = shippingSpeed === option.id;
              return (
                <div
                  key={option.id}
                  onClick={() => handleSpeedSelect(option.id)}
                  data-testid={`shipping-${option.id.toLowerCase()}`}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    isSelected 
                      ? `${option.borderColor} ${option.bgColor}` 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isSelected ? option.bgColor : 'bg-gray-100'
                    }`}>
                      <Icon className={`w-6 h-6 ${isSelected ? option.color : 'text-gray-400'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                          {option.name}
                        </p>
                        {option.id === 'SAME_DAY' && (
                          <Badge className="bg-emerald-500 text-white text-xs">Fastest</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {option.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${isSelected ? option.color : 'text-gray-600'}`}>
                        {useFreeDelivery ? (
                          <span className="text-green-500">FREE</span>
                        ) : (
                          `J$${option.price.toLocaleString()}`
                        )}
                      </p>
                      {isSelected && (
                        <CheckCircle className={`w-5 h-5 ${option.color} ml-auto mt-1`} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Validation indicator */}
          <div className={`mt-4 p-2 rounded-lg text-sm flex items-center gap-2 ${hasSpeed ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'}`}>
            {hasSpeed ? (
              <>
                <CheckCircle className="w-4 h-4" />
                {getSelectedShippingOption()?.name} selected
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4" />
                Select a shipping speed
              </>
            )}
          </div>
          
          {/* Optional Weight Input */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Scale className="h-4 w-4 text-gray-500" />
              <Label className="font-medium text-gray-700">Package Weight (optional)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                placeholder="e.g. 5"
                value={packageWeight}
                onChange={(e) => setPackageWeight(e.target.value)}
                className="w-32"
                data-testid="package-weight-input"
              />
              <span className="text-gray-500">lbs</span>
              <span className="text-xs text-gray-400 ml-2">
                Leave blank for default (10 lbs)
              </span>
            </div>
            {packageWeight && parseFloat(packageWeight) > 10 && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Packages over 10 lbs incur additional per-pound charges
              </p>
            )}
          </div>
        </div>

        {/* ========== STEP 3: DELIVERY WINDOW (Hidden for PUDO) ========== */}
        {deliveryMode !== 'PUDO' && (
        <div className="bg-white rounded-xl border border-emerald-100 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="h-5 w-5 text-emerald-500" />
            <h2 className="font-bold text-gray-800">Delivery Time</h2>
            <Badge variant="outline" className="ml-auto">Step 3</Badge>
          </div>
          
          {/* Scheduled Delivery Toggle */}
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Pre-book a delivery slot?</span>
              </div>
              <button
                onClick={() => {
                  setUseScheduledDelivery(!useScheduledDelivery);
                  if (useScheduledDelivery) setScheduledSlot(null);
                }}
                className={`relative w-11 h-6 rounded-full transition-colors ${useScheduledDelivery ? "bg-emerald-500" : "bg-gray-300"}`}
              >
                <span className={`relative w-11 h-6 rounded-full transition-colors ${useScheduledDelivery ? "bg-emerald-500" : "bg-gray-300"}`} />
              </button>
            </div>
            <p className="text-xs text-amber-700 mt-1">Book up to 7 days in advance for guaranteed delivery windows</p>
          </div>
          
          {/* Show ScheduledDelivery component if enabled */}
          {useScheduledDelivery ? (
            <ScheduledDelivery
              parish={parish}
              selectedSlot={scheduledSlot}
              onSlotSelect={(slot) => {
                setScheduledSlot(slot);
                setDeliveryDate(slot.date);
                // Map slot to preset
                if (slot.slot_id.includes('morning')) setDeliveryWindowPreset('morning');
                else if (slot.slot_id.includes('afternoon')) setDeliveryWindowPreset('afternoon');
                else setDeliveryWindowPreset('evening');
              }}
              className="mb-4"
            />
          ) : (
          <div className="space-y-4">
            {/* Date Selection */}
            <div>
              <Label className="text-gray-700">Delivery Date</Label>
              <Input
                type="date" data-testid="delivery-date-input"
                value={deliveryDate}
                onChange={(e) => handleDateChange(e.target.value)}
                min={getMinDate()}
                className="bg-white border-gray-200 h-11 rounded-lg mt-1"
              />
            </div>
            
            {/* Time Window Presets */}
            <div>
              <Label className="text-gray-700">Preferred Time Window</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {deliveryWindowPresets.map(preset => (
                  <button
                    key={preset.id}
                    data-testid={`time-window-${preset.id}`} onClick={() => handleWindowPresetChange(preset.id)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      deliveryWindowPreset === preset.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Clock className={`w-4 h-4 mx-auto mb-1 ${
                      deliveryWindowPreset === preset.id ? 'text-emerald-500' : 'text-gray-400'
                    }`} />
                    <p className={`text-xs font-medium ${
                      deliveryWindowPreset === preset.id ? 'text-emerald-700' : 'text-gray-600'
                    }`}>
                      {preset.id.charAt(0).toUpperCase() + preset.id.slice(1)}
                    </p>
                    <p className="text-xs text-gray-500">{preset.start}-{preset.end}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Delivery Instructions */}
            <div>
              <Label className="text-gray-700">Delivery Instructions (Optional)</Label>
              <Input
                placeholder="e.g., Leave at front door, call on arrival"
                value={deliveryInstructions}
                onChange={(e) => setDeliveryInstructions(e.target.value)}
                className="bg-white border-gray-200 h-11 rounded-lg mt-1"
              />
            </div>
          </div>
          )}
          
          {/* Blackout Error */}
          {blackoutError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{blackoutError}</p>
            </div>
          )}

          {/* Validation indicator */}
          <div className={`mt-4 p-2 rounded-lg text-sm flex items-center gap-2 ${hasDeliveryWindow ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'}`}>
            {hasDeliveryWindow ? (
              <>
                <CheckCircle className="w-4 h-4" />
                {new Date(deliveryDate).toLocaleDateString()} - {deliveryWindowPresets.find(p => p.id === deliveryWindowPreset)?.label || `${deliveryWindowStart}-${deliveryWindowEnd}`}
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4" />
                Select delivery date and time window
              </>
            )}
          </div>
        </div>
        )}

        {/* ========== CALCULATE SHIPPING BUTTON ========== */}
        <div className="bg-white rounded-xl border-2 border-dashed border-emerald-300 p-6">
          <div className="text-center">
            <Calculator className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-800 mb-2">Calculate Shipping</h3>
            <p className="text-sm text-gray-500 mb-4">
              {isReadyToQuote 
                ? 'All required fields selected. Click to calculate shipping cost.'
                : !hasAddress
                  ? '⚠️ Step 1: Enter your delivery address (street, city, parish)'
                  : !hasSpeed
                    ? '⚠️ Step 2: Select a shipping speed'
                    : deliveryMode === 'PUDO'
                      ? 'Complete steps 1-2 above to calculate shipping.'
                      : '⚠️ Step 3: Select delivery DATE and TIME WINDOW above'}
            </p>
            
            {checkout && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 justify-center">
                <RefreshCw className="w-4 h-4 text-yellow-600" />
                <p className="text-sm text-yellow-700">Quote ready. Change any field to recalculate.</p>
              </div>
            )}
            
            <Button
              onClick={handleCalculateShipping}
              disabled={!isReadyToQuote || calculating}
              className={`w-full h-12 rounded-xl font-medium ${
                isReadyToQuote 
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {calculating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Calculating...
                </>
              ) : checkout ? (
                <>
                  <RefreshCw className="mr-2 h-5 w-5" />
                  Recalculate Shipping
                </>
              ) : (
                <>
                  <Calculator className="mr-2 h-5 w-5" />
                  Calculate Shipping
                </>
              )}
            </Button>
          </div>
        </div>

        {/* ========== PROMO CODE (After calculation) ========== */}
        {checkout && (
          <div className="bg-white rounded-xl border border-emerald-100 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Tag className="h-5 w-5 text-emerald-500" />
              <h2 className="font-bold text-gray-800">Promo Code</h2>
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="promo-input"
                placeholder="Enter code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="bg-white border-gray-200 h-11 rounded-lg"
              />
              <Button
                onClick={applyPromo}
                variant="outline"
                className="border-emerald-500 text-emerald-600 rounded-lg px-6"
              >
                Apply
              </Button>
            </div>
            {promoApplied && checkout?.applied_incentive && (
              <p className="text-emerald-600 text-sm mt-2">
                ✓ {checkout.applied_incentive.title} applied!
              </p>
            )}
          </div>
        )}

        {/* ========== GIFT CARD (After promo code) ========== */}
        {checkout && (
          <div className="bg-gradient-to-r from-[#0E0E0E] to-[#009B3A]/80 rounded-xl border border-[#FCD116] p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Gift className="h-5 w-5 text-[#FCD116]" />
              <h2 className="font-bold text-white">Have a Gift Card?</h2>
              <span className="px-2 py-0.5 bg-[#FCD116] text-black text-xs font-bold rounded-full">NEW</span>
            </div>
            {!giftCardApplied ? (
              <div className="flex gap-2">
                <Input
                  placeholder="Enter gift card code (LTJA-XXXX-XXXX-XXXX)"
                  value={giftCardCode}
                  onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                  className="bg-white/10 border-[#FCD116]/50 text-white placeholder:text-gray-400 h-11 rounded-lg"
                />
                <Button
                  onClick={applyGiftCard}
                  disabled={giftCardLoading}
                  className="bg-[#FCD116] text-black hover:bg-[#E5BD14] rounded-lg px-6 font-bold"
                >
                  {giftCardLoading ? "Checking..." : "Apply"}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-mono">{giftCardApplied.code}</span>
                    <button onClick={removeGiftCard} className="text-red-400 hover:text-red-300 text-sm">Remove</button>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Gift card applied:</span>
                      <span className="text-[#FCD116] font-bold">-JMD ${giftCardApplied.applied_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Remaining to pay:</span>
                      <span className="text-white font-bold">JMD ${giftCardApplied.remaining_to_pay?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Card balance left:</span>
                      <span className="text-gray-400">JMD ${giftCardApplied.remaining_balance?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== PAYMENT METHOD (After calculation) ========== */}
        {checkout && (
          <div className="bg-white rounded-xl border border-emerald-100 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Wallet className="h-5 w-5 text-emerald-500" />
              <h2 className="font-bold text-gray-800">Payment Method</h2>
            </div>
            
            {/* Saved Cards */}
            {SAVED_CARDS_ENABLED && savedCards.length > 0 && (
              <div className="mb-4">
                <Label className="text-gray-700 text-sm">Saved Cards</Label>
                <div className="space-y-2 mt-2">
                  {savedCards.map(card => (
                    <div
                      key={card.id}
                      onClick={() => {
                        setSelectedSavedCard(card);
                        setPaymentMethod('stripe');
                      }}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedSavedCard?.id === card.id 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-emerald-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard className={`w-5 h-5 ${selectedSavedCard?.id === card.id ? 'text-emerald-500' : 'text-gray-400'}`} />
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">•••• {card.last4}</p>
                          <p className="text-xs text-gray-500">{card.brand} - Expires {card.exp_month}/{card.exp_year}</p>
                        </div>
                        {card.is_default && <Badge variant="secondary">Default</Badge>}
                        {selectedSavedCard?.id === card.id && (
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              {paymentMethods.map(method => {
                const Icon = method.icon;
                return (
                  <div
                    key={method.id}
                    onClick={() => {
                      setPaymentMethod(method.id);
                      if (method.id !== 'stripe') setSelectedSavedCard(null);
                    }}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      paymentMethod === method.id && !selectedSavedCard
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-gray-200 hover:border-emerald-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        paymentMethod === method.id && !selectedSavedCard ? 'bg-emerald-100' : 'bg-gray-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          paymentMethod === method.id && !selectedSavedCard ? 'text-emerald-600' : 'text-gray-500'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{method.name}</p>
                        <p className="text-xs text-gray-500">{method.description}</p>
                      </div>
                      {paymentMethod === method.id && !selectedSavedCard && (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Save Card Checkbox */}
            {SAVED_CARDS_ENABLED && paymentMethod === 'stripe' && !selectedSavedCard && (
              <div className="flex items-center gap-2 mt-4">
                <Checkbox 
                  id="save-card" 
                  checked={saveCard} 
                  onCheckedChange={setSaveCard}
                />
                <Label htmlFor="save-card" className="text-sm text-gray-600 cursor-pointer">
                  Save this card for future purchases
                </Label>
              </div>
            )}
          </div>
        )}

        {/* ========== ORDER SUMMARY (After calculation) ========== */}
        {checkout && (
          <div className="bg-white rounded-xl border border-emerald-100 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="h-5 w-5 text-emerald-500" />
              <h2 className="font-bold text-gray-800">Order Summary</h2>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal ({cart?.items?.length || 0} items)</span>
                <span>J${checkout.subtotal?.toLocaleString()}</span>
              </div>
              
              {applyPoints && pointsToApply > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span className="flex items-center gap-1">
                    <Coins className="w-4 h-4" />
                    Points Applied
                  </span>
                  <span>-J${pointsToApply.toLocaleString()}</span>
                </div>
              )}
              
              {checkout.discount_amount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span className="flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    Coupon Discount
                  </span>
                  <span>-J${checkout.discount_amount?.toLocaleString()}</span>
                </div>
              )}
              
              {/* Shipping Fee with Breakdown */}
              {checkout?.shipping_quote && (
                <div className="space-y-1">
                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-1">
                      <Truck className="w-4 h-4 text-emerald-500" />
                      Shipping ({checkout.shipping_quote.rule_used === "PUDO_FLAT" ? "PUDO" : "Delivery"})
                    </span>
                    {useFreeDelivery ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <Gift className="w-4 h-4" />
                        FREE
                      </span>
                    ) : (
                      <span className="font-medium">J${checkout.delivery_fee?.toLocaleString()}</span>
                    )}
                  </div>
                  
                  {/* Show breakdown details */}
                  {!useFreeDelivery && (
                    <div className="text-xs text-gray-500 ml-5 space-y-0.5">
                      {checkout.shipping_quote.rule_used === "PUDO_FLAT" ? (
                        <>
                          <div className="flex justify-between">
                            <span>Base Rate</span>
                            <span>J${checkout.shipping_quote.breakdown.pudo_flat_base_jmd?.toLocaleString()}</span>
                          </div>
                          {checkout.shipping_quote.breakdown.pudo_flat_overage_cost_jmd > 0 && (
                            <div className="flex justify-between text-amber-600">
                              <span>Weight Overage ({checkout.shipping_quote.breakdown.pudo_flat_overage_lbs} lbs)</span>
                              <span>+J${checkout.shipping_quote.breakdown.pudo_flat_overage_cost_jmd?.toLocaleString()}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span>Base Rate</span>
                            <span>J${checkout.shipping_quote.breakdown.base_cost_jmd?.toLocaleString()}</span>
                          </div>
                          {checkout.shipping_quote.breakdown.distance_cost_jmd > 0 && (
                            <div className="flex justify-between">
                              <span>Distance ({checkout.shipping_quote.distance?.miles} mi)</span>
                              <span>+J${checkout.shipping_quote.breakdown.distance_cost_jmd?.toLocaleString()}</span>
                            </div>
                          )}
                          {checkout.shipping_quote.breakdown.speed_multiplier_cost_jmd > 0 && (
                            <div className="flex justify-between text-blue-600">
                              <span>{checkout.shipping_quote.inputs?.service_level} Speed</span>
                              <span>+J${checkout.shipping_quote.breakdown.speed_multiplier_cost_jmd?.toLocaleString()}</span>
                            </div>
                          )}
                        </>
                      )}
                      {checkout.shipping_quote.messages?.length > 0 && (
                        <div className="text-amber-600 mt-1 text-xs">
                          ⚠️ {checkout.shipping_quote.messages[0]}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Fallback for old shipping display */}
              {!checkout?.shipping_quote && getSelectedShippingOption() && (
                <div className="flex justify-between text-gray-600">
                  <span className="flex items-center gap-1">
                    {(() => {
                      const opt = getSelectedShippingOption();
                      const Icon = opt.icon;
                      return <Icon className={`w-4 h-4 ${opt.color}`} />;
                    })()}
                    {getSelectedShippingOption().name}
                  </span>
                  {useFreeDelivery ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <Gift className="w-4 h-4" />
                      FREE
                    </span>
                  ) : (
                    <span>J${getSelectedShippingOption().price.toLocaleString()}</span>
                  )}
                </div>
              )}


                            {/* Delivery Window */}
              {deliveryDate && deliveryWindowPreset && (
                <div className="flex justify-between text-gray-600">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Delivery Window
                  </span>
                  <span className="text-sm">
                    {new Date(deliveryDate).toLocaleDateString()} - {deliveryWindowPresets.find(p => p.id === deliveryWindowPreset)?.label.split('(')[0]}
                  </span>
                </div>
              )}
              
              {/* Total Savings */}
              {(applyPoints || checkout.discount_amount > 0 || useFreeDelivery) && (
                <div className="py-2 px-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700 font-medium flex items-center gap-1">
                    <Gift className="w-4 h-4" />
                    Total Savings: J${(
                      (applyPoints ? pointsToApply : 0) + 
                      (checkout.discount_amount || 0) + 
                      (useFreeDelivery ? (getSelectedShippingOption()?.price || 0) : 0)
                    ).toLocaleString()}
                  </p>
                </div>
              )}
              
              <div className="border-t border-gray-100 pt-3 flex justify-between text-lg font-bold">
                <span className="text-gray-800">Total</span>
                <span className="text-emerald-600">
                  J${calculateTotal().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Secure Payment Badge */}
        <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
          <Shield className="w-4 h-4" />
          <span>Secure checkout by Liantiao</span>
        </div>

        {/* ========== STRIPE PAYMENT FORM ========== */}
        {showPaymentForm && paymentClientSecret && (
          <div className="bg-white rounded-xl border border-emerald-100 p-6 shadow-sm">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              Enter Card Details
            </h3>
            <StripePayment
              clientSecret={paymentClientSecret}
              amount={calculateTotal()}
              onSuccess={async (paymentIntentId) => {
                try {
                  await api.post(`/orders/${orderId}/confirm-payment`, null, {
                    params: { payment_reference: paymentIntentId }
                  });
                  setOrderComplete(true);
                  toast.success('Payment successful! Order confirmed.');
                } catch (err) {
                  toast.error('Payment confirmed but order update failed. Contact support.');
                }
              }}
              onError={(error) => {
                toast.error(error || 'Payment failed. Please try again.');
              }}
            />
          </div>
        )}

        {/* ========== PLACE ORDER BUTTON ========== */}
        {!showPaymentForm && (
        <Button
          data-testid="place-order-btn"
          onClick={handleCheckout}
          disabled={processing || !checkout}
          className={`w-full rounded-xl h-14 font-medium text-lg ${
            checkout 
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {processing ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Processing...
            </>
          ) : !checkout ? (
            'Calculate shipping to proceed'
          ) : paymentMethod === 'cod' ? (
            'Place Order (Pay on Delivery)'
          ) : (
            `Pay J$${calculateTotal().toLocaleString()}`
          )}
        </Button>
        )}
        
        <p className="text-center text-xs text-gray-500">
          By placing your order, you agree to our terms of service
        </p>
      </main>
    </div>
  );
};

export default Checkout;
